import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { AxiosError } from 'axios';

export const generateOTP = async ({ action }) => {
  const { data } = await apiClient.post('api-integrations/generate-otp', { action });
  return data;
};

export const viewSecret = async ({ otp }) => {
  const { data } = await apiClient.post('/api-integrations/view-secret', { otp });
  return data;
};

export const regenerateSecret = async ({ otp }) => {
  const { data } = await apiClient.post('/api-integrations/regenerate-secret', { otp });
  return data;
};

export const useGenerateOTPHandler = ({ onSuccess }) => {
  const dispatch = useDispatch();

  const mutation = useMutation({
    mutationFn: generateOTP,
    onSuccess: (data, variables) => {
      dispatch(
        showSnackbar({
          message: data.message || 'OTP sent to your email',
          severity: 'success',
        }),
      );
      onSuccess && onSuccess(variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        dispatch(
          showSnackbar({
            message: error.response?.data?.message || error.message,
            severity: 'error',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            message: 'Failed to send OTP. Please try again.',
            severity: 'error',
          }),
        );
      }
    },
  });

  const onSubmit = async (action) => {
    mutation.mutate({ action });
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error: mutation.error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    onSubmit: onSubmit,
  };
};

export const useViewSecretHandler = ({ onSuccess }) => {
  const dispatch = useDispatch();

  const mutation = useMutation({
    mutationFn: viewSecret,
    onSuccess: (data, variables) => {
      dispatch(
        showSnackbar({
          message: 'Secret revealed successfully',
          severity: 'success',
        }),
      );
      onSuccess && onSuccess(data, variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        dispatch(
          showSnackbar({
            message: error.response?.data?.errors?.otp || error.response?.data?.errors?.message || error.message,
            severity: 'error',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            message: 'Invalid OTP. Please try again.',
            severity: 'error',
          }),
        );
      }
    },
  });

  const onSubmit = async (otp) => {
    mutation.mutate({ otp });
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error: mutation.error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    onSubmit: onSubmit,
  };
};

export const useRegenerateSecretHandler = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: regenerateSecret,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['api-credentials'] });
      dispatch(
        showSnackbar({
          message: 'Secret regenerated successfully',
          severity: 'success',
        }),
      );
      onSuccess && onSuccess(data, variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        dispatch(
          showSnackbar({
            message: error.response?.data?.errors?.otp || error.response?.data?.errors?.message || error.message,
            severity: 'error',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            message: 'Invalid OTP. Please try again.',
            severity: 'error',
          }),
        );
      }
    },
  });

  const onSubmit = async (otp) => {
    mutation.mutate({ otp });
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error: mutation.error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    onSubmit: onSubmit,
  };
};
